import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import { useBranches } from '../branches/branchesApi';
import { useItems } from '../items/itemsApi';
import { useCreatePurchaseOrder, useSuppliers } from './purchasingApi';

interface Props {
  opened: boolean;
  onClose: () => void;
}

interface DraftLine {
  key: string;
  itemId: string | null;
  quantity: number | '';
  unitCost: number | '';
}

// PO create modal — supplier + branch + lines + optional expected date.
// Operator typically has <10 lines; modal-based form keeps the
// interaction quick without navigating away from the PO list.
export function CreatePurchaseOrderModal({ opened, onClose }: Props) {
  const { t } = useTranslation();
  const { data: branches } = useBranches();
  const { data: suppliers } = useSuppliers();
  const { data: items } = useItems();
  const create = useCreatePurchaseOrder();

  const [branchId, setBranchId] = useState<string | null>(null);
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [expectedAt, setExpectedAt] = useState<Date | null>(null);
  const [lines, setLines] = useState<DraftLine[]>([emptyLine()]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setBranchId(branches?.[0]?.branchId ?? null);
      setSupplierId(suppliers?.find((s) => s.isActive)?.supplierId ?? null);
      setReference('');
      setNotes('');
      setExpectedAt(null);
      setLines([emptyLine()]);
      setError(null);
    }
  }, [opened, branches, suppliers]);

  const itemOptions = useMemo(
    () => items?.map((i) => ({ value: i.itemId, label: `${i.sku} · ${i.nameAr}` })) ?? [],
    [items],
  );
  const supplierOptions = useMemo(
    () => suppliers?.filter((s) => s.isActive).map((s) => ({ value: s.supplierId, label: s.nameAr })) ?? [],
    [suppliers],
  );
  const branchOptions = useMemo(
    () => branches?.map((b) => ({ value: b.branchId, label: b.nameAr })) ?? [],
    [branches],
  );

  const total = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unitCost) || 0), 0),
    [lines],
  );

  const valid =
    !!branchId &&
    !!supplierId &&
    lines.length > 0 &&
    lines.every((l) => l.itemId && Number(l.quantity) > 0 && Number(l.unitCost) >= 0);

  const submit = async () => {
    if (!valid) return;
    setError(null);
    try {
      await create.mutateAsync({
        branchId: branchId!,
        supplierId: supplierId!,
        reference: reference.trim() || null,
        notes: notes.trim() || null,
        expectedAt: expectedAt ? expectedAt.toISOString() : null,
        lines: lines.map((l) => ({
          itemId: l.itemId!,
          quantity: Number(l.quantity),
          unitCost: Number(l.unitCost),
        })),
      });
      notifications.show({ color: 'green', title: t('purchasing.po.created'), message: '' });
      onClose();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail ?? (err instanceof Error ? err.message : t('purchasing.po.createFailed')));
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>{t('purchasing.po.create')}</Text>}
      size="xl"
      centered
    >
      <Stack gap="md">
        <Group grow>
          <Select
            label={t('purchasing.po.branch')}
            data={branchOptions}
            value={branchId}
            onChange={setBranchId}
            required
          />
          <Select
            label={t('purchasing.po.supplier')}
            data={supplierOptions}
            value={supplierId}
            onChange={setSupplierId}
            searchable
            required
          />
        </Group>
        <Group grow>
          <TextInput
            label={t('purchasing.po.reference')}
            description={t('purchasing.po.referenceHint')}
            value={reference}
            onChange={(e) => setReference(e.currentTarget.value)}
            dir="ltr"
          />
          <DatePickerInput
            label={t('purchasing.po.expectedAt')}
            value={expectedAt}
            onChange={setExpectedAt}
            clearable
            valueFormat="YYYY-MM-DD"
          />
        </Group>
        <Textarea
          label={t('purchasing.po.notes')}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          minRows={2}
        />

        <div>
          <Group justify="space-between" mb={6}>
            <Text fw={600} size="sm">{t('purchasing.po.lines')}</Text>
            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconPlus size={14} stroke={1.6} />}
              onClick={() => setLines((prev) => [...prev, emptyLine()])}
            >
              {t('purchasing.po.addLine')}
            </Button>
          </Group>
          <Table withTableBorder withColumnBorders striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ minWidth: 240 }}>{t('purchasing.po.item')}</Table.Th>
                <Table.Th style={{ width: 110 }}>{t('purchasing.po.qty')}</Table.Th>
                <Table.Th style={{ width: 120 }}>{t('purchasing.po.unitCost')}</Table.Th>
                <Table.Th style={{ width: 110 }} className="text-end">
                  {t('purchasing.po.lineTotal')}
                </Table.Th>
                <Table.Th style={{ width: 40 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {lines.map((line, idx) => {
                const lineTotal = (Number(line.quantity) || 0) * (Number(line.unitCost) || 0);
                return (
                  <Table.Tr key={line.key}>
                    <Table.Td>
                      <Select
                        data={itemOptions}
                        value={line.itemId}
                        onChange={(v) =>
                          setLines((prev) =>
                            prev.map((l, i) => (i === idx ? { ...l, itemId: v } : l)),
                          )
                        }
                        searchable
                        placeholder={t('purchasing.po.pickItem') ?? ''}
                        comboboxProps={{ withinPortal: true }}
                      />
                    </Table.Td>
                    <Table.Td>
                      {(() => {
                        // UOM conversion: the buyer types cartons, stock
                        // receives base units. Qty stays in BASE units in
                        // state; the helper shows the carton equivalent.
                        const selected = items?.find((i) => i.itemId === line.itemId);
                        const factor = selected?.purchaseUomFactor ?? 1;
                        const hasPurchaseUom = factor !== 1 && !!selected?.purchaseUomCode;
                        return (
                          <NumberInput
                            value={line.quantity}
                            onChange={(v) =>
                              setLines((prev) =>
                                prev.map((l, i) =>
                                  i === idx
                                    ? { ...l, quantity: typeof v === 'string' ? Number(v) : v }
                                    : l,
                                ),
                              )
                            }
                            min={0}
                            decimalScale={3}
                            description={
                              hasPurchaseUom && Number(line.quantity) > 0
                                ? `= ${(Number(line.quantity) / factor).toLocaleString('ar-PS', { maximumFractionDigits: 2, numberingSystem: 'latn' })} ${selected!.purchaseUomCode} (×${factor})`
                                : hasPurchaseUom
                                  ? `1 ${selected!.purchaseUomCode} = ${factor}`
                                  : undefined
                            }
                          />
                        );
                      })()}
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        value={line.unitCost}
                        onChange={(v) =>
                          setLines((prev) =>
                            prev.map((l, i) =>
                              i === idx
                                ? { ...l, unitCost: typeof v === 'string' ? Number(v) : v }
                                : l,
                            ),
                          )
                        }
                        min={0}
                        decimalScale={2}
                      />
                    </Table.Td>
                    <Table.Td className="font-numeric text-end">{formatMoney(lineTotal)}</Table.Td>
                    <Table.Td>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        disabled={lines.length === 1}
                        onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        <IconTrash size={16} stroke={1.6} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          <Group justify="flex-end" mt="xs">
            <Text size="sm" c="dimmed">{t('purchasing.po.total')}:</Text>
            <Text fw={600} className="font-numeric">{formatMoney(total)}</Text>
          </Group>
        </div>

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} stroke={1.6} />}>{error}</Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={create.isPending}>
            {t('common.cancel')}
          </Button>
          <Button color="wheels" onClick={submit} disabled={!valid} loading={create.isPending}>
            {t('purchasing.po.submit')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function emptyLine(): DraftLine {
  return {
    key: crypto.randomUUID(),
    itemId: null,
    quantity: '',
    unitCost: '',
  };
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'ILS',
    minimumFractionDigits: 2,
  }).format(value);
}
